import { AlertTriangle } from "lucide-react";

// Confirmation modal shown before any drag-drop stage change. Blocks
// moves past ``form_submitted`` when the customer hasn't yet signed
// the online wizard, and warns (without blocking) if a plan mismatch
// is unacknowledged and the target stage would activate + bill.

export function MoveConfirmModal({ pending, stages, onConfirm, onCancel }) {
  const from = stages.find(s => s.key === pending.from);
  const to = stages.find(s => s.key === pending.to);
  const client = pending.client;
  const stageKeys = stages.map(s => s.key);
  const toIdx = stageKeys.indexOf(pending.to);
  const fsIdx = stageKeys.indexOf("form_submitted");
  const postSignup = fsIdx >= 0 && toIdx >= fsIdx;
  // Signup gate satisfied by EITHER the wizard link OR a manual
  // signed-form upload attached via the drawer.
  const hasSignup = !!(client.linked_signup_id || client.manual_signup);
  const blocked = postSignup && !hasSignup;
  const mismatch = client.plan_mismatch || {};
  const mismatchWarning = !blocked && fsIdx >= 0 && toIdx > fsIdx
    && mismatch.is_mismatch && !mismatch.acknowledged;
  return (
    <div className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onCancel}>
      <div
        className="w-full max-w-md rounded-xl border border-slate-800 bg-slate-900 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="move-confirm-modal"
      >
        <div className="p-5 border-b border-slate-800">
          <h3 className="font-display text-lg tracking-tight text-slate-100">
            {blocked ? "Signup required" : "Move client?"}
          </h3>
        </div>
        <div className="p-5 space-y-4">
          <div className="text-sm text-slate-300">
            <span className="font-semibold">{client.business_name || client.contact_name || client.email}</span>
          </div>
          <div className="flex items-center gap-3 justify-center py-2">
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-800 bg-slate-950">
              <span className="w-2 h-2 rounded-full" style={{ background: from?.color || "#64748b" }} />
              <span className="text-xs uppercase tracking-widest text-slate-400 font-semibold">{from?.label || pending.from}</span>
            </div>
            <span className="text-slate-500">→</span>
            <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${blocked ? "border-red-500/50 bg-red-500/10" : "border-cyan-500/50 bg-cyan-500/10"}`}>
              <span className="w-2 h-2 rounded-full" style={{ background: to?.color || "#22d3ee" }} />
              <span className={`text-xs uppercase tracking-widest font-semibold ${blocked ? "text-red-300" : "text-cyan-300"}`}>{to?.label || pending.to}</span>
            </div>
          </div>
          {blocked && (
            <div className="rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-200" data-testid="move-blocked-msg">
              This client hasn&apos;t completed the online signup yet. They must sign off through the transfer form before moving to <span className="font-semibold">{to?.label || pending.to}</span> or later stages.
              <div className="text-xs text-slate-400 mt-2">
                Send them the signup link on the top of this board, or if they&apos;ve already signed with a different email use <span className="text-slate-200 font-medium">Manual link to signup</span> in the client drawer. Signed a paper/PDF form? Open the drawer and <span className="text-slate-200 font-medium">Upload signed form</span> — it counts the same.
              </div>
            </div>
          )}
          {mismatchWarning && (
            <div className="rounded-lg border border-red-500/50 bg-red-500/10 p-3 text-sm text-red-200" data-testid="move-mismatch-warning">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-red-300 shrink-0 mt-0.5" />
                <div>
                  <div className="font-semibold text-red-100">Plan mismatch not yet acknowledged</div>
                  <div className="text-xs text-red-200/90 mt-1">
                    This customer&apos;s signed plan does not match what they&apos;re currently on.
                    Moving them to <span className="font-semibold">{to?.label || pending.to}</span> means we&apos;re about
                    to activate + bill. Please open the drawer, review the comparison, and acknowledge
                    before proceeding — or move ahead if you&apos;re certain.
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="p-5 border-t border-slate-800 flex items-center justify-end gap-2">
          <button
            data-testid="move-confirm-cancel"
            onClick={onCancel}
            className="px-4 py-2 rounded-lg border border-slate-800 text-sm text-slate-300 hover:bg-slate-800"
          >
            {blocked ? "Close" : "Cancel"}
          </button>
          {!blocked && (
            <button
              data-testid="move-confirm-ok"
              onClick={onConfirm}
              className="px-4 py-2 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-white text-sm font-medium"
            >
              Move
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
